import { Fragment, useEffect, useState } from 'react';
import { BehaviorSubject } from 'rxjs';
import { kebabCase, startCase } from 'lodash';
import { ComponentSpinner } from '../Loading/ComponentSpinner';
import { UpstackDataGrid } from '../UpstackDataGrid/UpstackDataGrid';
import { ChangeHistoryRecord } from '../../../store/ChangeHistory';
import { DEFAULT_MINI_TABLE_SX_STYLES } from '../../../utils/styleHelpers';
import { ApiAdvancedFilter, PaginatedResponse } from '../../../store/GeneralStore';
import { DetailViewCard } from '../CardWithTitle';
import { DefaultHistoryGridColumnDefs } from '../../Pages/History/HistoryGridColumnDefs';

export interface HistoryMiniTableParams {
  id: string;
  fetchFunction: (
    filters?: ApiAdvancedFilter,
    forceRefresh?: boolean
  ) => Promise<PaginatedResponse<ChangeHistoryRecord> | undefined | void>;
  dataSubject: () => BehaviorSubject<PaginatedResponse<ChangeHistoryRecord>>;
  loadingSubject: () => BehaviorSubject<boolean>;
  recordType: 'asset' | 'provisional_asset' | 'ticket';
}

export const HistoryMiniTable = ({
  id,
  fetchFunction,
  dataSubject,
  loadingSubject,
  recordType
}: HistoryMiniTableParams) => {
  const [history, setHistory] = useState<PaginatedResponse<ChangeHistoryRecord>>(
    {} as PaginatedResponse<ChangeHistoryRecord>
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [tableLoading, setTableLoading] = useState<boolean>(true);

  const fetchHistory = async (filters?: ApiAdvancedFilter, loading?: boolean, forceRefresh?: boolean) => {
    fetchFunction(
      {
        page: 1,
        pageSize: 50,
        ...filters,
        filters: [
          ...(filters?.filters || []),
          {
            field: 'recordId',
            operator: 'equals',
            value: id
          },
          {
            field: 'recordType',
            operator: 'equals',
            value: recordType
          }
        ]
      },
      forceRefresh
    );
  };

  useEffect(() => {
    const dataSub = dataSubject().subscribe((r) => {
      if (!tableLoading) setHistory(r);
      setLoading(false);
    });

    const loadingSub = loadingSubject().subscribe((loading) => setTableLoading(loading));

    return () => {
      if (dataSub) dataSub.unsubscribe();
      if (loadingSub) loadingSub.unsubscribe();
    };
  }, [tableLoading]);

  useEffect(() => {
    if (id) fetchHistory(undefined, false, true);
  }, [id]);

  const sxStyle = {
    ...DEFAULT_MINI_TABLE_SX_STYLES,
    minHeight: 'inherit',
    height: 'inherit',
    maxHeight: 'calc(100vh - 300px)'
  };

  const filteredCols = DefaultHistoryGridColumnDefs().filter(
    (col) =>
      col.field !== 'recordTitle' &&
      col.field !== 'recordType' &&
      (recordType !== 'ticket' || col.field !== 'revenueChangeCategory')
  );
  const filterKeys = filteredCols.filter((c) => c.filterable).map((c) => c.field);

  return (
    <DetailViewCard
      enableCollapse
      key="history-table-card"
      title={`${startCase(kebabCase(recordType))} History`}
      dataTestId="history-view-card">
      <Fragment>
        {loading && <ComponentSpinner label="Loading History Data" />}
        {!loading && (
          <UpstackDataGrid
            rows={history.data || []}
            sxStyles={sxStyle}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: 50 } },
              sorting: { sortModel: [{ field: 'changedAt', sort: 'desc' }] },
              pinnedColumns: { left: ['changedAt'] },
              columns: {
                columnVisibilityModel: {
                  changeSource: false,
                  operation: false
                }
              }
            }}
            filterKeys={filterKeys}
            currentPage={history.page}
            rowCount={history.totalCount || 0}
            columns={filteredCols}
            dataCy="history-mini-data-grid-pagination"
            fetchFunction={fetchHistory}
            title="history"
            showSearch={false}
            page="historyGrid_MiniTable"
            apiFilter
            gridSettings={{ muiConfig: {}, disabledFields: [] }}
            pageSizeOptions={[50, 100, 250]}
            showToolbar={false}
            withoutSave
            localLoading={tableLoading}
            pagination={!!history.totalCount}
            advancedFiltering
          />
        )}
      </Fragment>
    </DetailViewCard>
  );
};
